import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, Calendar as CalendarIcon, Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { db } from "../../firebase/config";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
  startAfter,
  startAt,
  endAt,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";

interface BorrowRecord {
  id: string;
  bookId: string;
  bookTitle: string;
  memberId: string;
  memberName: string;
  borrowDate: string;
  dueDate: string;
  returnDate?: string;
  status: "borrowed" | "returned";
  fineAmount: number;
  renewCount: number;
  maxRenewals: number;
  lookupId: string;
}

interface Member {
  id: string;
  name: string;
  memberId: string;
  role: "student" | "staff";
  borrowedBooks: number;
  maxBorrowLimit: number;
}

interface ActivityLogEntry {
  id: string;
  timestamp: string;
  action: "borrow" | "return" | "reserve" | "cancel_reserve" | "renew" | "fine_payment" | "overdue";
  bookId: string;
  bookTitle: string;
  memberId: string;
  memberName: string;
  staffId?: string;
  staffName?: string;
  details: string;
  amount?: number;
  role?: "student" | "staff";
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

export const ActivityLog = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [borrowRecords, setBorrowRecords] = useState<BorrowRecord[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAction, setFilterAction] = useState("all");
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  
  // Server-side Pagination State
  const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [pageStack, setPageStack] = useState<QueryDocumentSnapshot<DocumentData>[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 10;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [schoolID, setSchoolID] = useState<string | null>(null);

  // Helper to fetch total count
  const fetchTotalCount = async (schoolId: string, search: string) => {
    try {
      const recordsRef = collection(db, `schools/${schoolId}/borrowRecords`);
      let q = query(recordsRef);
      if (search) {
        q = query(recordsRef, orderBy("bookTitle"), startAt(search), endAt(search + '\uf8ff'));
      }
      const snapshot = await getCountFromServer(q);
      setTotalRecords(snapshot.data().count);
    } catch (err) {
      console.error("Error fetching count:", err);
    }
  };

  // Auth Effect - Sets schoolID and fetches members
  useEffect(() => {
    let unsubscribeAuth: (() => void) | undefined;
    let unsubscribeStudents: (() => void) | undefined;
    let unsubscribeTeachers: (() => void) | undefined;

    const auth = getAuth();
    unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false);
        toast({
          title: "Authentication Error",
          description: "No user logged in. Please log in and try again.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult(true);
        const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
        const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
        const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

        let selectedAffiliation = defaultAffiliation && (defaultAffiliation.role === "admin" || defaultAffiliation.role === "school_staff") && defaultAffiliation.schoolID
          ? defaultAffiliation
          : affiliations.find((aff) => (aff.role === "admin" || aff.role === "school_staff") && aff.schoolID) || null;

        if (!selectedAffiliation || selectedAffiliation.schoolID !== claimSchoolID) {
          const retryAffiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          selectedAffiliation = retryAffiliations.find((aff) => (aff.role === "admin" || aff.role === "school_staff") && aff.schoolID) || null;

          if (!selectedAffiliation || selectedAffiliation.schoolID !== claimSchoolID) {
            throw new Error(
              `Invalid token claims: affiliations=${JSON.stringify(retryAffiliations)}, defaultAffiliation=${JSON.stringify(
                defaultAffiliation
              )}, schoolID=${claimSchoolID}`
            );
          }
        }

        const selectedSchoolID = selectedAffiliation.schoolID || null;
        setSchoolID(selectedSchoolID);

        if (selectedSchoolID) {
          // Fetch Students
          const studentsRef = collection(db, `schools/${selectedSchoolID}/students`);
          const studentsQuery = query(studentsRef, orderBy("createdAt", "desc"), limit(1000));
          unsubscribeStudents = onSnapshot(studentsQuery, (snapshot) => {
            const studentData = snapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                name: `${data.firstName || ""} ${data.lastName || ""}`.trim() || "Unknown",
                memberId: data.registrationNumber || "",
                role: "student" as const,
                borrowedBooks: data.borrowedBooks || 0,
                maxBorrowLimit: 3,
              } as Member;
            });
            setMembers((prev) => [...prev.filter((m) => m.role === "staff"), ...studentData]);
          });

          // Fetch Teachers
          const teachersRef = collection(db, `schools/${selectedSchoolID}/teachers`);
          const teachersQuery = query(teachersRef, orderBy("timestamp", "desc"), limit(1000));
          unsubscribeTeachers = onSnapshot(teachersQuery, (snapshot) => {
            const teacherData = snapshot.docs.map((doc) => {
              const data = doc.data();
              return {
                id: data.teacherId || doc.id,
                name: `${data.firstName || ""} ${data.lastName || ""}`.trim() || "Unknown",
                memberId: data.employeeId || "",
                role: "staff" as const,
                borrowedBooks: data.borrowedBooks || 0,
                maxBorrowLimit: 5,
              } as Member;
            });
            setMembers((prev) => [...prev.filter((m) => m.role === "student"), ...teacherData]);
          });

          setLoading(false);
        } else {
          throw new Error("No school affiliation found.");
        }
      } catch (error: any) {
        console.error("Error verifying user claims:", error);
        setLoading(false);
        setError("Failed to verify permissions. Please contact support.");
        toast({
          title: "Missing Permissions",
          description: "You lack the necessary permissions to view activity logs. Please contact support.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      }
    });

    return () => {
      unsubscribeAuth?.();
      unsubscribeStudents?.();
      unsubscribeTeachers?.();
    };
  }, [navigate, toast]);

  // Separate Effect for Paginated Borrow Records Fetching (like BooksManagement)
  useEffect(() => {
    if (!schoolID) return;

    let unsubscribe: () => void;

    const load = async () => {
      setLoading(true);
      const recordsRef = collection(db, `schools/${schoolID}/borrowRecords`);
      let q;

      // Build query based on search and pagination
      if (searchTerm.trim()) {
        q = query(recordsRef, orderBy("bookTitle"), startAt(searchTerm), endAt(searchTerm + '\uf8ff'));
      } else {
        q = query(recordsRef, orderBy("borrowDate", "desc"));
      }

      // Apply pagination cursor
      if (currentPage > 1 && pageStack.length >= currentPage - 1) {
        const cursor = pageStack[currentPage - 2];
        if (searchTerm.trim()) {
          q = query(recordsRef, orderBy("bookTitle"), startAt(searchTerm), endAt(searchTerm + '\uf8ff'), startAfter(cursor), limit(itemsPerPage));
        } else {
          q = query(recordsRef, orderBy("borrowDate", "desc"), startAfter(cursor), limit(itemsPerPage));
        }
      } else {
        // Page 1
        if (searchTerm.trim()) {
          q = query(recordsRef, orderBy("bookTitle"), startAt(searchTerm), endAt(searchTerm + '\uf8ff'), limit(itemsPerPage));
        } else {
          q = query(recordsRef, orderBy("borrowDate", "desc"), limit(itemsPerPage));
        }
      }

      unsubscribe = onSnapshot(q, (snapshot) => {
        const recordData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            bookId: data.bookId,
            bookTitle: data.bookTitle,
            memberId: data.memberId,
            memberName: data.memberName,
            borrowDate: data.borrowDate.toDate().toISOString().split("T")[0],
            dueDate: data.dueDate.toDate().toISOString().split("T")[0],
            returnDate: data.returnDate ? data.returnDate.toDate().toISOString().split("T")[0] : undefined,
            status: data.status,
            fineAmount: data.fineAmount || 0,
            renewCount: data.renewCount || 0,
            maxRenewals: data.maxRenewals || 2,
            lookupId: data.lookupId || data.memberId,
          } as BorrowRecord;
        });
        setBorrowRecords(recordData);

        if (snapshot.docs.length > 0) {
          setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
        } else {
          setLastVisible(null);
        }
        setLoading(false);
      }, (err) => {
        console.error("Error fetching records:", err);
        setError("Failed to load activity records");
        setLoading(false);
      });

      // Update count
      fetchTotalCount(schoolID, searchTerm);
    };

    load();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [schoolID, searchTerm, currentPage]); // NOT pageStack

  const activities = useMemo(() => {
    const memberRoleMap = new Map<string, string>();
    members.forEach(member => {
      memberRoleMap.set(member.id, member.role);
      if (member.memberId) {
        memberRoleMap.set(member.memberId, member.role);
      }
    });

    return borrowRecords
      .map((record) => {
        let action: ActivityLogEntry["action"];
        let timestamp: string;
        let details: string;
        let amount: number | undefined;

        const today = new Date();
        const effectiveStatus = record.status === "returned" ? "returned" : (today > new Date(record.dueDate) ? "overdue" : "borrowed");

        const roleValue = memberRoleMap.get(record.id) || memberRoleMap.get(record.memberId) || memberRoleMap.get(record.lookupId);
        const role: "student" | "staff" | undefined = roleValue === "student" || roleValue === "staff" ? roleValue : "student";

        if (record.status === "returned") {
          action = "return";
          timestamp = record.returnDate || record.borrowDate;
          details = `Book returned. ${record.fineAmount > 0 ? `Fine: $${record.fineAmount.toFixed(2)}` : 'Returned on time'}`;
          amount = record.fineAmount;
        } else if (effectiveStatus === "overdue") {
          action = "overdue";
          timestamp = record.borrowDate;
          details = `Book marked as overdue. Fine applied: $${record.fineAmount.toFixed(2)}`;
          amount = record.fineAmount;
        } else {
          action = "borrow";
          timestamp = record.borrowDate;
          details = `Book borrowed successfully. Due date: ${record.dueDate}`;
          amount = undefined;
        }

        if (record.renewCount > 0) {
          const renewActivity: ActivityLogEntry = {
            id: `${record.id}_renew`,
            timestamp: record.dueDate,
            action: "renew",
            bookId: record.bookId,
            bookTitle: record.bookTitle,
            memberId: record.memberId,
            memberName: record.memberName,
            details: `Book renewed ${record.renewCount} time(s). New due date: ${record.dueDate}`,
            amount: undefined,
            role,
          };
          return [renewActivity, { ...record, action, timestamp, details, amount, role } as ActivityLogEntry];
        }

        return { ...record, action, timestamp, details, amount, role } as ActivityLogEntry;
      })
      .flat()
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [borrowRecords, members]);

  // Handlers
  const handleNextPage = () => {
    if (borrowRecords.length < itemsPerPage) return; // Can't go next if current page is not full
    if (lastVisible) {
      setPageStack(prev => [...prev, lastVisible]);
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setPageStack(prev => prev.slice(0, -1));
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    setPageStack([]);
  };

  // Client-side filtering on the fetched page
  const filteredActivities = activities.filter(activity => {
    const matchesAction = filterAction === "all" || activity.action === filterAction;

    const activityDate = new Date(activity.timestamp);
    const matchesDateRange = (!dateRange.from || activityDate >= dateRange.from) &&
      (!dateRange.to || activityDate <= dateRange.to);

    return matchesAction && matchesDateRange;
  });

  const estimatedTotalPages = Math.ceil(totalRecords / itemsPerPage);

  const getActionBadge = (action: string) => {
    let badgeClass = "";
    let label = action.charAt(0).toUpperCase() + action.slice(1).replace("_", " ");

    switch (action) {
      case "borrow":
        badgeClass = "bg-[#22C55E] dark:bg-[#4ADE80] hover:bg-[#16A34A] dark:hover:bg-[#22C55E] text-white";
        break;
      case "return":
        badgeClass = "bg-[#6B7280] dark:bg-[#9CA3AF] hover:bg-[#4B5563] dark:hover:bg-[#6B7280] text-white";
        break;
      case "reserve":
        badgeClass = "bg-[#3B82F6] dark:bg-[#60A5FA] hover:bg-[#2563EB] dark:hover:bg-[#3B82F6] text-white";
        break;
      case "cancel_reserve":
        badgeClass = "bg-[#EF4444] dark:bg-[#F87171] hover:bg-[#DC2626] dark:hover:bg-[#EF4444] text-white";
        break;
      case "renew":
        badgeClass = "bg-[#2563EB] dark:bg-[#3B82F6] hover:bg-[#1E40AF] dark:hover:bg-[#2563EB] text-white";
        break;
      case "fine_payment":
        badgeClass = "bg-[#10B981] dark:bg-[#34D399] hover:bg-[#059669] dark:hover:bg-[#10B981] text-white";
        break;
      case "overdue":
        badgeClass = "bg-[#F59E0B] dark:bg-[#FBBF24] hover:bg-[#D97706] dark:hover:bg-[#F59E0B] text-white";
        break;
      default:
        badgeClass = "bg-[#6B7280] dark:bg-[#9CA3AF] hover:bg-[#4B5563] dark:hover:bg-[#6B7280] text-white";
    }

    return (
      <Badge className={`text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate ${badgeClass}`}>
        {label}
      </Badge>
    );
  };

  const getRoleBadge = (role: "student" | "staff") => {
    const badgeClass = role === "staff"
      ? "bg-[#2563EB] dark:bg-[#3B82F6] hover:bg-[#1E40AF] dark:hover:bg-[#2563EB] text-white"
      : "bg-[#6B7280] dark:bg-[#9CA3AF] hover:bg-[#4B5563] dark:hover:bg-[#6B7280] text-white";
    return (
      <Badge className={`text-xs px-3 py-0.5 font-medium w-fit max-w-[120px] flex items-center justify-center box-border text-center truncate ${badgeClass}`}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </Badge>
    );
  };

  const exportActivityLog = () => {
    const csvContent = [
      ["Timestamp", "Action", "Book", "Member", "Role", "Details", "Amount"].join(","),
      ...filteredActivities.map(activity => [
        activity.timestamp,
        activity.action,
        activity.bookTitle,
        `${activity.memberName} (${activity.memberId})`,
        activity.role || "",
        activity.details,
        activity.amount?.toFixed(2) || ""
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `library_activity_log_${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="flex flex-col space-y-4 h-full min-h-0 flex-1 justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="flex flex-col space-y-4 h-full min-h-0 flex-1 justify-center items-center">
          <p className="text-foreground text-center">{error}</p>
          <Button onClick={() => window.location.reload()} className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-2 px-4 rounded-full flex items-center gap-2">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
          .font-roboto {
            font-family: 'Roboto', sans-serif;
          }
          /* Mobile-only fix – prevents black overscroll bar */
          @media (max-width: 767px) {
            body { overflow-x: hidden; }
          }
          .no-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .no-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          /* Mobile-only full-width scroll container */
          @media (max-width: 767px) {
            .mobile-table-container {
              width: 100vw;
              margin-left: calc(-50vw + 50%);
            }
          }
        `}
      </style>
      <div className="w-full max-w-full min-h-0 flex flex-col p-4 sm:p-6 lg:p-8 overflow-hidden bg-gray-50 dark:bg-gray-900 font-roboto">
        <div className="flex flex-col space-y-4 h-full min-h-0">
          {/* Controls */}
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between flex-shrink-0">
            <div className="flex flex-col sm:flex-row gap-4 flex-1 w-full lg:w-auto">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search by book title..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10 bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:border-[#CBD5E1] dark:hover:border-[#334155] focus:border-[#2563EB] dark:focus:border-[#3B82F6] h-10"
                />
              </div>

              <Select value={filterAction} onValueChange={(value) => { setFilterAction(value); setCurrentPage(1); }}>
                <SelectTrigger className="w-full sm:w-48 font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                  <SelectValue placeholder="Filter by action" />
                </SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                  <SelectItem value="all" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">All Actions</SelectItem>
                  <SelectItem value="borrow" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Borrow</SelectItem>
                  <SelectItem value="return" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Return</SelectItem>
                  <SelectItem value="reserve" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Reserve</SelectItem>
                  <SelectItem value="cancel_reserve" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Cancel Reserve</SelectItem>
                  <SelectItem value="renew" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Renew</SelectItem>
                  <SelectItem value="fine_payment" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Fine Payment</SelectItem>
                  <SelectItem value="overdue" className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">Overdue</SelectItem>
                </SelectContent>
              </Select>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="m-2 w-full sm:w-auto justify-start text-left font-medium bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                    {dateRange.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, "LLL dd, y")} -{" "}
                          {format(dateRange.to, "LLL dd, y")}
                        </>
                      ) : (
                        format(dateRange.from, "LLL dd, y")
                      )
                    ) : (
                      "Pick a date range"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange.from}
                    selected={{ from: dateRange.from, to: dateRange.to }}
                    onSelect={(range) => { setDateRange({ from: range?.from, to: range?.to }); setCurrentPage(1); }}
                    numberOfMonths={2}
                    className="bg-[#FFFFFF] dark:bg-[#020817] text-black dark:text-white"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <Button
              variant="outline"
              onClick={exportActivityLog}
              className="m-2 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] h-10"
            >
              <Download className="h-4 w-4 mr-2 text-muted-foreground" />
              Export Log
            </Button>
          </div>

          <div className="flex-1 min-h-0">
            {/* Activity Log Table */}
            <Card className="h-full flex flex-col bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-black dark:text-white">Activity Log</CardTitle>
              </CardHeader>
              <CardContent className="p-0 flex-1 min-h-0 overflow-hidden">
                <div className="h-full overflow-auto no-scrollbar">
                  <div className="mobile-table-container overflow-x-auto md:overflow-x-visible">
                    <Table className="w-full">
                      <TableHeader>
                        <TableRow className="border-b border-[#F1F5F9] dark:border-[#1E293B]">
                          <TableHead className="min-w-[100px] text-black dark:text-white">Timestamp</TableHead>
                          <TableHead className="min-w-[80px] text-black dark:text-white">Action</TableHead>
                          <TableHead className="min-w-[140px] text-black dark:text-white">Book</TableHead>
                          <TableHead className="min-w-[140px] text-black dark:text-white">Member</TableHead>
                          <TableHead className="min-w-[100px] text-black dark:text-white">Role</TableHead>
                          <TableHead className="min-w-[180px] text-black dark:text-white">Details</TableHead>
                          <TableHead className="min-w-[70px] text-right text-black dark:text-white">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredActivities.map((activity) => (
                          <TableRow key={activity.id} className="border-b border-[#F1F5F9] dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#101929]">
                            <TableCell className="font-mono text-sm text-black dark:text-white py-2">
                              {format(new Date(activity.timestamp), "MMM dd, yyyy HH:mm")}
                            </TableCell>
                            <TableCell className="py-2">{getActionBadge(activity.action)}</TableCell>
                            <TableCell className="font-medium text-black dark:text-white py-2">
                              <div className="truncate max-w-[140px]">{activity.bookTitle}</div>
                            </TableCell>
                            <TableCell className="py-2">
                              <div>
                                <div className="font-medium text-black dark:text-white truncate max-w-[140px]">{activity.memberName}</div>
                                <div className="text-sm text-muted-foreground truncate max-w-[140px]">{activity.memberId}</div>
                              </div>
                            </TableCell>
                            <TableCell className="text-black dark:text-white py-2">
                              {activity.role ? getRoleBadge(activity.role) : "-"}
                            </TableCell>
                            <TableCell className="max-w-[180px] truncate text-black dark:text-white py-2">
                              {activity.details}
                            </TableCell>
                            <TableCell className="text-right text-black dark:text-white py-2">
                              {activity.amount ? `$${activity.amount.toFixed(2)}` : "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pagination Controls */}
          <div className="flex flex-col items-center gap-4 pt-4 border-t border-[#F1F5F9] dark:border-[#1E293B]">
            <div className="text-sm text-muted-foreground text-center">
              Page {currentPage} {totalRecords > 0 ? `of ~${estimatedTotalPages}` : ''} ({borrowRecords.length} shown)
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-blue-600 text-white">
                {currentPage}
              </span>
              <button
                onClick={handleNextPage}
                disabled={borrowRecords.length < itemsPerPage}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-[#F1F5F9] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#101828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>

          {filteredActivities.length === 0 && !loading && (
            <Card className="bg-[#FFFFFF] dark:bg-[#020817] border-[#F1F5F9] dark:border-[#1E293B]">
              <CardContent className="text-center py-12">
                <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground">No activity found</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  Try adjusting your search filters or date range
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};